import React, { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import "../assets/css/ServiceCategoryPicker.css";

/**
 * Multi-category service picker: tick one or more categories, then pick services
 * (sub-categories) under each. Unticking a category clears its picked services.
 */
function ServiceCategoryPicker({
  categories = [],
  customServices = {},
  selected = [],
  onToggleService,
  onChangeServices,
  onDeleteCustomService,
}) {
  const [openIds, setOpenIds] = useState([]);

  const servicesOf = (categoryId) => {
    const cat = categories.find((c) => c.category_id === categoryId);
    const subs = (cat?.subcategories || []).map((s) => s.subcategory_id);
    const custom = Object.entries(customServices)
      .filter(([, s]) => s.categoryId === categoryId)
      .map(([id]) => id);
    return [...subs, ...custom];
  };

  // Open every category that already has a picked service (edit mode, prefilled leads).
  useEffect(() => {
    if (!selected.length || !categories.length) return;
    const withPicks = categories
      .filter((c) => servicesOf(c.category_id).some((id) => selected.includes(id)))
      .map((c) => c.category_id);
    setOpenIds((prev) => {
      const missing = withPicks.filter((id) => !prev.includes(id));
      return missing.length ? [...prev, ...missing] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, categories, customServices]);

  const toggleCategory = (categoryId) => {
    if (openIds.includes(categoryId)) {
      const drop = servicesOf(categoryId);
      setOpenIds((prev) => prev.filter((id) => id !== categoryId));
      if (drop.some((id) => selected.includes(id))) {
        onChangeServices(selected.filter((id) => !drop.includes(id)));
      }
    } else {
      setOpenIds((prev) => [...prev, categoryId]);
    }
  };

  const openCategories = categories.filter((c) => openIds.includes(c.category_id));

  return (
    <div className="services-segregated-compact scp">
      <div className="scp-category-list">
        {categories.map((cat) => {
          const active = openIds.includes(cat.category_id);
          const count = servicesOf(cat.category_id).filter((id) => selected.includes(id)).length;
          return (
            <label key={cat.category_id} className={`scp-category${active ? " scp-category--active" : ""}`}>
              <input type="checkbox" checked={active} onChange={() => toggleCategory(cat.category_id)} />
              <span>{cat.category_name}</span>
              {count ? <span className="scp-count">{count}</span> : null}
            </label>
          );
        })}
      </div>

      <div className="services-subcategory-compact">
        {!openCategories.length ? (
          <div className="services-subcategory-empty">Select one or more categories to see services.</div>
        ) : (
          openCategories.map((cat) => {
            const subs = cat.subcategories || [];
            const custom = Object.entries(customServices)
              .filter(([, s]) => s.categoryId === cat.category_id)
              .map(([id, s]) => ({ id, name: s.name }));
            return (
              <div key={cat.category_id} className="scp-group">
                <div className="services-subcategory-list-label">{cat.category_name}</div>
                {subs.length || custom.length ? (
                  <div className="services-subcategory-grid scp-grid">
                    {subs.map((sub) => (
                      <label key={sub.subcategory_id} className="service-checkbox service-checkbox-compact">
                        <input
                          type="checkbox"
                          checked={selected.includes(sub.subcategory_id)}
                          onChange={() => onToggleService(sub.subcategory_id)}
                        />
                        <span>{sub.subcategory_name}</span>
                      </label>
                    ))}
                    {custom.map(({ id, name }) => (
                      <div key={id} className="addlead-custom-service-row">
                        <label className="service-checkbox service-checkbox-compact">
                          <input type="checkbox" checked={selected.includes(id)} onChange={() => onToggleService(id)} />
                          <span>{name}</span>
                        </label>
                        {onDeleteCustomService ? (
                          <button
                            type="button"
                            className="addlead-custom-service-delete"
                            onClick={() => onDeleteCustomService(id)}
                            title="Remove"
                          >
                            <FaTrash size={12} />
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="services-subcategory-empty">No services in this category.</div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ServiceCategoryPicker;
